// components/admin/VisaTypesForm.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Eye,
  Save,
  AlertCircle,
  Filter,
  Download,
  Calendar,
  CreditCard,
  Clock,
  Globe,
  FileText,
  CheckCircle,
  Star,
  Zap,
} from "lucide-react";

const VisaTypesForm = ({ country, onClose, onSuccess }) => {
  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" or "form"
  const [editingId, setEditingId] = useState(null); // Track which item is being edited
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [filterType, setFilterType] = useState("all"); // "all", "popular", "express"
  const [searchQuery, setSearchQuery] = useState("");
  const [newDocInput, setNewDocInput] = useState("");

  const [newVisaType, setNewVisaType] = useState({
    name: "",
    type: "Single Entry",
    validity: "30 Days",
    duration: "30 Days",
    processingTime: "7 Business Days",
    estimatedDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    price: "₹2790",
    express: false,
    popular: false,
    requiredDocuments: ["Passport", "Photograph", "Proof of Funds"],
  });

  // Filter and search functionality
  const filteredVisaTypes = visaTypes.filter((visa) => {
    // Apply type filter
    if (filterType === "popular" && !visa.popular) return false;
    if (filterType === "express" && !visa.express) return false;

    // Apply search filter
    if (
      searchQuery &&
      !visa.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  // FETCH VISATYPES FOR THE COUNTRY - UPDATED VERSION
  const fetchVisaTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `${import.meta.env.VITE_URL}/api/admin/visa-info`
      );

      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

      const data = await res.json();

      // Handle response data based on the new structure
      let countryVisa = null;

      if (data.visas && Array.isArray(data.visas)) {
        // Find the country's visa info from the visas array
        countryVisa = data.visas.find(
          (item) =>
            item._id === country._id ||
            item.country === country.name ||
            (item.country &&
              country.name &&
              item.country.toLowerCase() === country.name.toLowerCase())
        );
      } else if (Array.isArray(data)) {
        // Fallback: if response is directly an array
        countryVisa = data.find(
          (item) => item._id === country._id || item.country === country.name
        );
      } else if (data.data && Array.isArray(data.data)) {
        // Another fallback structure
        countryVisa = data.data.find(
          (item) => item._id === country._id || item.country === country.name
        );
      }

      if (
        countryVisa &&
        countryVisa.visaType &&
        Array.isArray(countryVisa.visaType)
      ) {
        // Ensure each visa type has a unique ID
        const processedVisaTypes = countryVisa.visaType.map((visa, index) => ({
          ...visa,
          // Convert numeric IDs to strings for consistency
          id: String(visa?.id || visa?._id || `visa-${index}`),
          _id: String(visa?._id || visa?.id || `visa-${index}`),
        }));

        setVisaTypes(processedVisaTypes);
      } else {
        setVisaTypes([]);
      }
    } catch (err) {
      console.error("Error fetching visa types:", err);
      setError(`Failed to load visa types: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (country && country._id) {
      fetchVisaTypes();
    }
  }, [country]);

  // ADD NEW VISA TYPE - UPDATED TO MATCH YOUR API STRUCTURE
  const handleAddVisaType = async (e) => {
    e.preventDefault();

    // Validate
    if (!newVisaType.name.trim()) {
      alert("Please enter a visa name");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Generate temp ID for immediate UI update
      const tempId = `temp-${Date.now()}`;
      const newVisa = {
        ...newVisaType,
        _id: tempId,
        id: tempId,
      };

      // Optimistic update
      setVisaTypes((prev) => [...prev, newVisa]);

      // Prepare data for API - match your expected structure
      const visaData = {
        // Send minimal data for new visa type
        name: newVisaType.name,
        type: newVisaType.type,
        validity: newVisaType.validity,
        duration: newVisaType.duration,
        processingTime: newVisaType.processingTime,
        estimatedDate: newVisaType.estimatedDate,
        price: newVisaType.price,
        express: newVisaType.express,
        popular: newVisaType.popular,
        requiredDocuments: newVisaType.requiredDocuments,
      };

      // Make API call to add visa type
      const res = await fetch(
        `${import.meta.env.VITE_URL}/api/admin/add-type-visa/${
          country._id
        }/visa-types`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(visaData),
        }
      );

      const responseData = await res.json();

      if (!res.ok) {
        throw new Error(responseData.message || "Failed to add visa type");
      }

      // Refresh the list from server
      await fetchVisaTypes();

      // Reset form
      setNewVisaType({
        name: "",
        type: "Single Entry",
        validity: "30 Days",
        duration: "30 Days",
        processingTime: "7 Business Days",
        estimatedDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        price: "₹2790",
        express: false,
        popular: false,
        requiredDocuments: ["Passport", "Photograph", "Proof of Funds"],
      });

      setViewMode("list");
      onSuccess?.();
    } catch (err) {
      console.error("Error adding visa type:", err);
      setError(`Failed to add visa type: ${err.message}`);

      // Revert optimistic update on error
      setVisaTypes((prev) =>
        prev.filter((v) => !String(v._id).includes("temp-"))
      );
    } finally {
      setSaving(false);
    }
  };

  // UPDATE EXISTING VISA TYPE - FIXED VERSION
  const handleUpdateVisaType = async (e) => {
    e.preventDefault();

    if (!editingId) {
      return;
    }

    // Find the current visa being edited
    const currentVisa = visaTypes.find(
      (v) =>
        String(v._id) === String(editingId) ||
        String(v.id) === String(editingId)
    );

    if (!currentVisa) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // First update the visa types array locally
      const updatedVisaTypes = visaTypes.map((v) =>
        String(v._id) === String(editingId) ||
        String(v.id) === String(editingId)
          ? currentVisa // Use the current visa with updated fields
          : v
      );

      // Prepare data for API update
      // We need to update the entire country visa info with the updated visaTypes array
      const updatedData = {
        _id: country._id,
        name: country.name,
        visaType: updatedVisaTypes.map((visa) => ({
          // Keep the existing structure
          _id: visa._id,
          id: visa.id,
          name: visa.name,
          type: visa.type,
          validity: visa.validity,
          duration: visa.duration,
          processingTime: visa.processingTime,
          estimatedDate: visa.estimatedDate,
          price: visa.price,
          express: visa.express,
          popular: visa.popular,
          requiredDocuments: visa.requiredDocuments,
        })),
      };

      // Update the entire country visa info document
      const res = await fetch(
        `${import.meta.env.VITE_URL}/api/admin/visa-info/${country._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update visa type");
      }

      // const responseData = await res.json();

      await fetchVisaTypes(); // Refresh data
      setEditingId(null);
      setViewMode("list");
      onSuccess?.();
    } catch (err) {
      console.error("Error updating visa type:", err);
      setError(`Failed to update visa type: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // DELETE VISA TYPE
  const handleDeleteVisaType = async (id) => {
    if (!window.confirm("Are you sure you want to delete this visa type?"))
      return;

    setSaving(true);
    setError(null);

    try {
      // Find the visa type to delete
      const visaToDelete = visaTypes.find(
        (v) => String(v._id) === String(id) || String(v.id) === String(id)
      );

      if (!visaToDelete) {
        throw new Error("Visa type not found");
      }

      // Check if it's a temp visa (not yet saved to server)
      if (visaToDelete._id && String(visaToDelete._id).includes("temp-")) {
        // Just remove from local state
        setVisaTypes((prev) =>
          prev.filter(
            (v) => String(v._id) !== String(id) && String(v.id) !== String(id)
          )
        );
        setShowDeleteConfirm(null);
        return;
      }

      // For existing visas, update the country's visaType array without the deleted item
      const updatedVisaTypes = visaTypes.filter(
        (v) => String(v._id) !== String(id) && String(v.id) !== String(id)
      );

      const updatedCountryData = {
        ...country,
        visaType: updatedVisaTypes,
      };

      // Update the country document
      const res = await fetch(
        `${import.meta.env.VITE_URL}/api/admin/visa-countries/type/${
          country._id
        }`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedCountryData),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to delete visa type");
      }

      // Update local state
      setVisaTypes(updatedVisaTypes);
      setShowDeleteConfirm(null);
      onSuccess?.();
    } catch (err) {
      console.error("Error deleting visa type:", err);
      setError(`Failed to delete visa type: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // START EDITING
  const startEditVisaType = (id) => {
    setEditingId(String(id));
    setViewMode("form");
  };

  // UPDATE LOCAL STATE DURING EDITING
  const updateVisaTypeField = (field, value) => {
    if (!editingId) return;

    setVisaTypes((prev) =>
      prev.map((visa) =>
        String(visa?._id) === String(editingId) ||
        String(visa?.id) === String(editingId)
          ? { ...visa, [field]: value }
          : visa
      )
    );
  };

  // CANCEL EDITING
  const cancelEdit = () => {
    setEditingId(null);
    setViewMode("list");
    fetchVisaTypes(); // Reload original data
  };

  // EXPORT TO CSV
  const handleExportCSV = () => {
    const headers = [
      "Name",
      "Type",
      "Validity",
      "Duration",
      "Processing Time",
      "Price",
      "Popular",
      "Express",
      "Documents",
    ];
    const csvRows = [
      headers.join(","),
      ...filteredVisaTypes.map((visa) =>
        [
          `"${visa.name}"`,
          `"${visa.type}"`,
          `"${visa.validity}"`,
          `"${visa.duration}"`,
          `"${visa.processingTime}"`,
          `"${visa.price}"`,
          visa.popular ? "Yes" : "No",
          visa.express ? "Yes" : "No",
          `"${visa.requiredDocuments?.join(", ") || ""}"`,
        ].join(",")
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${country.name.replace(/\s+/g, "_")}_visa_types.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // RENDER LIST VIEW
  const renderListView = () => {
    return (
      <div className="space-y-6">
        {/* Header with Stats and Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Visa Types Management
            </h3>
            <p className="text-gray-600 mt-1">
              Managing {visaTypes.length} visa types for {country.name}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleExportCSV}
              disabled={visaTypes.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={() => {
                setEditingId(null);
                setViewMode("form");
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md"
            >
              <Plus size={20} />
              Add New Visa Type
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-blue-700">Total Visa Types</p>
                <p className="text-2xl font-bold text-gray-900">
                  {visaTypes.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-yellow-700">Popular</p>
                <p className="text-2xl font-bold text-gray-900">
                  {visaTypes.filter((v) => v.popular).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-purple-700">Express</p>
                <p className="text-2xl font-bold text-gray-900">
                  {visaTypes.filter((v) => v.express).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <Eye className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search visa types..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterType("all")}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filterType === "all"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All Types
              </button>
              <button
                onClick={() => setFilterType("popular")}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  filterType === "popular"
                    ? "bg-yellow-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Star size={16} />
                Popular
              </button>
              <button
                onClick={() => setFilterType("express")}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  filterType === "express"
                    ? "bg-purple-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Zap size={16} />
                Express
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 shadow-sm">
            <AlertCircle size={20} className="flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Visa Types Grid */}
        {filteredVisaTypes.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-2xl bg-gradient-to-b from-gray-50 to-white">
            <Globe className="mx-auto w-16 h-16 text-gray-400 mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || filterType !== "all"
                ? "No matching results"
                : "No visa types found"}
            </h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchQuery || filterType !== "all"
                ? "Try adjusting your search or filter criteria"
                : `Start by adding your first visa type for ${country.name}`}
            </p>
            <button
              onClick={() => {
                setEditingId(null);
                setViewMode("form");
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
            >
              <Plus size={20} />
              Add First Visa Type
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredVisaTypes.map((visa) => (
              <div
                key={visa?._id || visa?.id}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:border-blue-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-gray-900 text-lg">
                        {visa.name}
                      </h4>
                      <div className="flex gap-2">
                        {visa.popular && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 text-xs font-semibold rounded-full border border-yellow-200">
                            <Star size={12} />
                            Popular
                          </span>
                        )}
                        {visa.express && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-purple-50 to-purple-100 text-purple-800 text-xs font-semibold rounded-full border border-purple-200">
                            <Zap size={12} />
                            Express
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Globe size={14} />
                      <span className="text-sm">{visa.type}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEditVisaType(visa?._id || visa?.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteVisaType(visa?._id || visa?.id)
                      }
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                      disabled={saving}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Visa Details Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Processing Time</p>
                      <p className="font-medium text-sm">
                        {visa.processingTime}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Validity</p>
                      <p className="font-medium text-sm">{visa.validity}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="font-medium text-sm">{visa.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Price</p>
                      <p className="font-medium text-sm text-green-600">
                        {visa.price}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Documents */}
                {visa?.requiredDocuments &&
                  visa?.requiredDocuments?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={16} className="text-gray-500" />
                        <p className="text-sm font-medium text-gray-700">
                          Required Documents
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {visa.requiredDocuments.slice(0, 3).map((doc, idx) => (
                          <span
                            key={idx}
                            className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-100"
                          >
                            {doc}
                          </span>
                        ))}
                        {visa.requiredDocuments.length > 3 && (
                          <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg">
                            +{visa.requiredDocuments.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                {/* Status Badge */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {visa?._id && String(visa._id).includes("temp-") ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-50 text-yellow-700 text-xs font-medium rounded-full">
                          <AlertCircle size={12} />
                          Unsaved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                          <CheckCircle size={12} />
                          Saved
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      Last updated:{" "}
                      {new Date().toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // RENDER FORM VIEW
  const renderFormView = () => {
    const isEdit = editingId !== null;
    const currentVisa = isEdit
      ? visaTypes.find(
          (v) =>
            String(v._id) === String(editingId) ||
            String(v.id) === String(editingId)
        )
      : newVisaType;

    if (isEdit && !currentVisa) {
      return (
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <p className="text-gray-900 font-medium text-lg mb-2">
            Visa type not found
          </p>
          <p className="text-gray-600 mb-6">
            The visa type you're trying to edit doesn't exist
          </p>
          <button
            onClick={() => setViewMode("list")}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to List
          </button>
        </div>
      );
    }

    return (
      <form
        onSubmit={isEdit ? handleUpdateVisaType : handleAddVisaType}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {isEdit ? "Edit Visa Type" : "Create New Visa Type"}
            </h3>
            <p className="text-gray-600 mt-1">
              {isEdit
                ? "Update the details of this visa type"
                : "Fill in the details to add a new visa type"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              <Eye size={18} />
              View All
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            <div className="flex items-center gap-2">
              <AlertCircle size={20} />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={20} />
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visa Name *
                </label>
                <input
                  type="text"
                  value={currentVisa.name || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("name", e.target.value)
                      : setNewVisaType({ ...newVisaType, name: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., Tourist Visa, Business Visa"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visa Type
                </label>
                <select
                  value={currentVisa.type || "Single Entry"}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("type", e.target.value)
                      : setNewVisaType({ ...newVisaType, type: e.target.value })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                >
                  <option value="Single Entry">Single Entry</option>
                  <option value="Multiple Entry">Multiple Entry</option>
                  <option value="Double Entry">Double Entry</option>
                  <option value="Transit">Transit</option>
                  <option value="Business">Business</option>
                  <option value="Student">Student</option>
                </select>
              </div>
            </div>
          </div>

          {/* Duration & Validity */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock size={20} />
              Duration & Validity
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validity Period
                </label>
                <input
                  type="text"
                  value={currentVisa.validity || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("validity", e.target.value)
                      : setNewVisaType({
                          ...newVisaType,
                          validity: e.target.value,
                        })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., 30 Days, 6 Months"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stay Duration
                </label>
                <input
                  type="text"
                  value={currentVisa.duration || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("duration", e.target.value)
                      : setNewVisaType({
                          ...newVisaType,
                          duration: e.target.value,
                        })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., 30 Days, 90 Days"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Processing Time
                </label>
                <input
                  type="text"
                  value={currentVisa.processingTime || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("processingTime", e.target.value)
                      : setNewVisaType({
                          ...newVisaType,
                          processingTime: e.target.value,
                        })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., 7 Business Days"
                />
              </div>
            </div>
          </div>

          {/* Price & Features */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard size={20} />
              Price & Features
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price
                </label>
                <input
                  type="text"
                  value={currentVisa.price || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("price", e.target.value)
                      : setNewVisaType({
                          ...newVisaType,
                          price: e.target.value,
                        })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., ₹2790, $150"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Date
                </label>
                <input
                  type="date"
                  value={currentVisa.estimatedDate || ""}
                  onChange={(e) =>
                    isEdit
                      ? updateVisaTypeField("estimatedDate", e.target.value)
                      : setNewVisaType({
                          ...newVisaType,
                          estimatedDate: e.target.value,
                        })
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 p-4 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={currentVisa.popular || false}
                    onChange={(e) =>
                      isEdit
                        ? updateVisaTypeField("popular", e.target.checked)
                        : setNewVisaType({
                            ...newVisaType,
                            popular: e.target.checked,
                          })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </div>
                <div className="flex items-center gap-2">
                  <Star size={18} className="text-yellow-600" />
                  <span className="font-medium text-gray-700">
                    Mark as Popular
                  </span>
                  <span className="text-sm text-gray-500">
                    (Featured visa type)
                  </span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={currentVisa.express || false}
                    onChange={(e) =>
                      isEdit
                        ? updateVisaTypeField("express", e.target.checked)
                        : setNewVisaType({
                            ...newVisaType,
                            express: e.target.checked,
                          })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </div>
                <div className="flex items-center gap-2">
                  <Zap size={18} className="text-purple-600" />
                  <span className="font-medium text-gray-700">
                    Express Service
                  </span>
                  <span className="text-sm text-gray-500">
                    (Faster processing)
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Required Documents */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={20} />
              Required Documents
            </h4>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-4">
                Type the name of a required document and click Add or press Enter:
              </p>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newDocInput}
                  onChange={(e) => setNewDocInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newDocInput.trim()) {
                        const docs = currentVisa.requiredDocuments || [];
                        if (!docs.includes(newDocInput.trim())) {
                          const updatedDocs = [...docs, newDocInput.trim()];
                          if (isEdit) {
                            updateVisaTypeField("requiredDocuments", updatedDocs);
                          } else {
                            setNewVisaType({
                              ...newVisaType,
                              requiredDocuments: updatedDocs,
                            });
                          }
                        }
                        setNewDocInput("");
                      }
                    }
                  }}
                  placeholder="e.g., Driving Licence"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newDocInput.trim()) {
                      const docs = currentVisa.requiredDocuments || [];
                      if (!docs.includes(newDocInput.trim())) {
                        const updatedDocs = [...docs, newDocInput.trim()];
                        if (isEdit) {
                          updateVisaTypeField("requiredDocuments", updatedDocs);
                        } else {
                          setNewVisaType({
                            ...newVisaType,
                            requiredDocuments: updatedDocs,
                          });
                        }
                      }
                      setNewDocInput("");
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus size={18} /> Add
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {(currentVisa.requiredDocuments || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No documents added yet.</p>
                ) : (
                  (currentVisa.requiredDocuments || []).map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-full text-sm font-medium">
                      <span>{doc}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const docs = currentVisa.requiredDocuments || [];
                          const updatedDocs = docs.filter(d => d !== doc);
                          if (isEdit) {
                            updateVisaTypeField("requiredDocuments", updatedDocs);
                          } else {
                            setNewVisaType({
                              ...newVisaType,
                              requiredDocuments: updatedDocs,
                            });
                          }
                        }}
                        className="p-0.5 hover:bg-blue-200 rounded-full text-blue-500 hover:text-blue-800 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            {isEdit &&
              currentVisa?._id &&
              String(currentVisa._id).includes("temp-") && (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 text-sm font-medium rounded-lg border border-yellow-200">
                  <AlertCircle size={16} />
                  Unsaved changes
                </span>
              )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={isEdit ? cancelEdit : () => setViewMode("list")}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {isEdit ? "Saving Changes..." : "Adding Visa Type..."}
                </>
              ) : (
                <>
                  <Save size={20} />
                  {isEdit ? "Save Changes" : "Add Visa Type"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-800 to-gray-900 p-6 border-b border-gray-700">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white">
                Visa Types Management
              </h2>
              <p className="text-gray-300 mt-2 flex items-center gap-2">
                <Globe size={16} />
                {country.name}
                <span className="ml-2 px-2.5 py-0.5 bg-gray-700 text-gray-300 text-sm font-medium rounded-full">
                  {visaTypes.length} visa types
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-white"
              disabled={saving}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] bg-gray-50">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-500 border-t-transparent"></div>
              </div>
              <h3 className="mt-6 text-lg font-semibold text-gray-900">
                Loading Visa Types
              </h3>
              <p className="text-gray-600 mt-2">
                Please wait while we fetch the visa types data...
              </p>
            </div>
          ) : viewMode === "list" ? (
            renderListView()
          ) : (
            renderFormView()
          )}
        </div>
      </div>
    </div>
  );
};

export default VisaTypesForm;
